import React from "react";
import { ThreeEvent, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { editorStore } from "../../../store/editorStore";
import { FloorplannerStoreContext } from "../../../store/floorplannerStore";
import type { FloorplannerStore } from "../../../store/floorplannerStore";
import { renderStore } from "../../../store/renderStore";
import {
  SymbolType,
  WallType,
  isSymbolType,
} from "../../../types/floorplannerTypes";
import {
  canPlaceSymbol3DAtPosition,
  createSymbol3DFootprintPolygon,
  isDraggableLoose3DSymbol,
  resolveSymbol3DDragPosition,
} from "./symbol3DDragCollision";
import { resolveSymbolPlacementFeatureSnap } from "../../../utils/symbolPlacementFeatureSnap";

/** Magnet for object/wall footprint snaps during precise 3D placement (~0.3 m). */
const FEATURE_SNAP_TOLERANCE_MULTIPLIER = 3;

const DRAG_MOVE_THRESHOLD_PX = 2;
const FLOOR_PLANE = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);

type Symbol3DDragState = {
  pointerId: number;
  clientX: number;
  clientY: number;
  pointerTarget?: {
    releasePointerCapture?: (pointerId: number) => void;
  };
  startPoint: THREE.Vector3;
  startPosition: THREE.Vector2;
  lastValidPosition: THREE.Vector2;
  visualState: Symbol3DDragVisualState;
  groupedSymbols: Map<string, GroupedSymbolDragState>;
  groupedAttachmentWallIds: Set<string>;
  previousBlockDirty: boolean | null;
  /** True when siblings move via renderStore group-translate (no MobX per frame). */
  usingGroupTranslate: boolean;
  dragging: boolean;
};

type GroupedSymbolDragState = {
  startPosition: THREE.Vector2;
};

export type Symbol3DDragVisualState = {
  position: THREE.Vector2;
  threeD?: SymbolType["threeD"];
};

type UseSelectedSymbol3DDragInput = {
  symbol: SymbolType;
  selected: boolean;
  walls: WallType[];
  onDragActiveChange?: (active: boolean) => void;
  visualTargetRef?: React.RefObject<THREE.Object3D | null>;
  applyVisualState?: (
    object: THREE.Object3D,
    state: Symbol3DDragVisualState,
  ) => void;
  persistThreeDOnDrag?: boolean;
};

export function useSelectedSymbol3DDrag({
  symbol,
  selected,
  walls,
  onDragActiveChange,
  visualTargetRef,
  applyVisualState,
  persistThreeDOnDrag = true,
}: UseSelectedSymbol3DDragInput) {
  const floorplannerStore = React.useContext(FloorplannerStoreContext);
  const { camera, gl, invalidate } = useThree();
  const dragStateRef = React.useRef<Symbol3DDragState | null>(null);
  const draggable =
    selected &&
    !editorStore.panning &&
    !editorStore.ruler3DConstructionMode &&
    !floorplannerStore.lockedByOther &&
    isDraggableLoose3DSymbol(symbol);

  const finishDrag = React.useCallback(
    (event?: ThreeEvent<PointerEvent> | PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      if (event && event.pointerId !== dragState.pointerId) return;

      stopOrbitControlEvent(event);
      releasePointerCapture(dragState);
      dragStateRef.current = null;
      document.body.style.cursor = "auto";

      if (dragState.dragging) {
        const moved = dragState.visualState.position.distanceToSquared(
          dragState.startPosition,
        );
        const offset = dragState.visualState.position
          .clone()
          .sub(dragState.startPosition);

        if (moved > 1e-10) {
          // Commit store positions before clearing visual group-translate offset
          // so meshes don't flash back to their start positions for a frame.
          commitGroupedSymbolOffset(
            floorplannerStore,
            dragState,
            symbol.id,
            offset,
          );
          floorplannerStore.updateSymbolProperty(symbol.id, "position", [
            dragState.visualState.position.x,
            dragState.visualState.position.y,
          ]);
          if (persistThreeDOnDrag && dragState.visualState.threeD) {
            floorplannerStore.updateSymbolProperty(
              symbol.id,
              "threeD",
              dragState.visualState.threeD,
            );
          }
          endGroupTranslateIfNeeded(dragState);
          dragState.groupedSymbols.forEach((_, symbolId) => {
            floorplannerStore.recordSymbolChange(symbolId, "modify");
          });
          dragState.groupedAttachmentWallIds.forEach((wallId) => {
            floorplannerStore.recordWallChange(wallId, "modify");
          });
          restoreDirtyBlocking(floorplannerStore, dragState);
          floorplannerStore.setDirty();
          floorplannerStore.pushToUndoStack();
        } else {
          endGroupTranslateIfNeeded(dragState);
          restoreDirtyBlocking(floorplannerStore, dragState);
          floorplannerStore.cancelUndoTransaction();
        }
        onDragActiveChange?.(false);
      }

      invalidate();
    },
    [
      floorplannerStore,
      invalidate,
      onDragActiveChange,
      persistThreeDOnDrag,
      symbol.id,
    ],
  );

  const handlePointerDown = React.useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (!draggable || event.button !== 0) return;
      if (event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      const startPoint = getPointerPointOnFloorPlane(event);
      if (!startPoint) return;
      const groupedSymbols = getCoMovingSymbolDragStates(
        floorplannerStore,
        symbol,
      );

      stopOrbitControlEvent(event);
      setPointerCapture(event);
      dragStateRef.current = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
        pointerTarget: event.target as unknown as {
          releasePointerCapture?: (pointerId: number) => void;
        },
        startPoint,
        startPosition: symbol.position.clone(),
        lastValidPosition: symbol.position.clone(),
        visualState: {
          position: symbol.position.clone(),
          threeD: symbol.threeD,
        },
        groupedSymbols,
        groupedAttachmentWallIds: getAttachmentWallIds(
          floorplannerStore,
          groupedSymbols,
        ),
        previousBlockDirty: null,
        usingGroupTranslate: false,
        dragging: false,
      };
    },
    [draggable, floorplannerStore, symbol],
  );

  const moveDrag = React.useCallback(
    (
      event: ThreeEvent<PointerEvent> | PointerEvent,
      currentPoint: THREE.Vector3 | null,
    ) => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) return;

      stopOrbitControlEvent(event);

      if (!dragState.dragging) {
        const movedFarEnough =
          Math.abs(event.clientX - dragState.clientX) >
            DRAG_MOVE_THRESHOLD_PX ||
          Math.abs(event.clientY - dragState.clientY) > DRAG_MOVE_THRESHOLD_PX;

        if (!movedFarEnough) return;

        dragState.dragging = true;
        document.body.style.cursor = "grabbing";
        floorplannerStore.beginUndoTransaction("drag 3D symbol");
        dragState.groupedSymbols.forEach((_, symbolId) => {
          floorplannerStore.recordSymbolForUndo(symbolId);
        });
        dragState.groupedAttachmentWallIds.forEach((wallId) => {
          floorplannerStore.recordWallForUndo(wallId);
        });
        if (dragState.groupedSymbols.size > 1) {
          dragState.previousBlockDirty = floorplannerStore.blockDirty;
          floorplannerStore.setBlockDirty(true);
          // Visual-only sibling motion (same pattern as 2D group drag).
          renderStore.beginGroupTranslate(
            Array.from(dragState.groupedSymbols.keys()),
            Array.from(dragState.groupedAttachmentWallIds),
          );
          dragState.usingGroupTranslate = true;
        }
        onDragActiveChange?.(true);
      }

      if (!currentPoint) return;

      const delta = new THREE.Vector2(
        currentPoint.x - dragState.startPoint.x,
        currentPoint.y - dragState.startPoint.y,
      );
      const candidatePosition = dragState.startPosition.clone().add(delta);
      const bypassCollision = event.shiftKey;
      if (
        symbol.threeD?.placementMode === "wall" &&
        symbol.threeD.wallMount?.wallId
      ) {
        const wall = walls.find(
          (candidate) => candidate.id === symbol.threeD?.wallMount?.wallId,
        );
        if (wall) {
          const nextWallState = projectPointToWall(candidatePosition, wall);
          dragState.lastValidPosition = nextWallState.position.clone();
          applyNextVisualState(
            dragState,
            visualTargetRef?.current,
            applyVisualState,
            {
              position: nextWallState.position.clone(),
              threeD: {
                ...symbol.threeD,
                wallMount: {
                  ...symbol.threeD.wallMount,
                  offset: nextWallState.offset,
                },
              },
            },
          );
          const wallOffset = nextWallState.position
            .clone()
            .sub(dragState.startPosition);
          updateGroupTranslateOffsetIfNeeded(dragState, wallOffset);
          invalidate();
          return;
        }
      }
      let nextPosition = resolveSymbol3DDragPosition({
        symbol,
        candidatePosition,
        lastValidPosition: dragState.lastValidPosition,
        walls,
        wallShapesById: renderStore.innerWallShapesMap,
        defaultWallWidth: floorplannerStore.wallWidth,
        wallSnapTolerance: editorStore.snappingTolerance * 5,
        wallSnapReferencePosition: dragState.startPosition,
        bypassCollision,
      });
      if (!bypassCollision) {
        const featureSnapTolerance =
          editorStore.snappingTolerance * FEATURE_SNAP_TOLERANCE_MULTIPLIER;
        nextPosition = resolveSymbolPlacementFeatureSnap({
          symbol,
          position: nextPosition,
          walls,
          wallShapesById: renderStore.innerWallShapesMap,
          defaultWallWidth: floorplannerStore.wallWidth,
          otherSymbols: Array.from(floorplannerStore.symbolsMap.values()),
          tolerance: featureSnapTolerance,
          getFootprintPolygon: (candidate, position) =>
            createSymbol3DFootprintPolygon(candidate, position, walls),
          canPlaceAt: (position) =>
            canPlaceSymbol3DAtPosition({
              symbol,
              candidatePosition: position,
              walls,
              wallShapesById: renderStore.innerWallShapesMap,
              defaultWallWidth: floorplannerStore.wallWidth,
              wallSnapTolerance: editorStore.snappingTolerance * 5,
              wallSnapReferencePosition: dragState.startPosition,
            }),
        });
        dragState.lastValidPosition = nextPosition.clone();
      }

      applyNextVisualState(
        dragState,
        visualTargetRef?.current,
        applyVisualState,
        {
          position: nextPosition.clone(),
        },
      );
      updateGroupTranslateOffsetIfNeeded(
        dragState,
        nextPosition.clone().sub(dragState.startPosition),
      );
      invalidate();
    },
    [
      applyVisualState,
      floorplannerStore,
      floorplannerStore.wallWidth,
      invalidate,
      onDragActiveChange,
      symbol,
      visualTargetRef,
      walls,
    ],
  );

  const handlePointerMove = React.useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      moveDrag(event, getPointerPointOnFloorPlane(event));
    },
    [moveDrag],
  );

  React.useEffect(() => {
    const handleWindowPointerMove = (event: PointerEvent) => {
      if (
        !dragStateRef.current ||
        event.pointerId !== dragStateRef.current.pointerId
      ) {
        return;
      }
      moveDrag(
        event,
        getPointerPointOnFloorPlaneFromNative(event, camera, gl.domElement),
      );
    };
    const handleWindowPointerUp = (event: PointerEvent) => {
      finishDrag(event);
    };
    const handleWindowPointerCancel = (event: PointerEvent) => {
      finishDrag(event);
    };

    window.addEventListener("pointermove", handleWindowPointerMove, true);
    window.addEventListener("pointerup", handleWindowPointerUp, true);
    window.addEventListener("pointercancel", handleWindowPointerCancel, true);

    return () => {
      window.removeEventListener("pointermove", handleWindowPointerMove, true);
      window.removeEventListener("pointerup", handleWindowPointerUp, true);
      window.removeEventListener(
        "pointercancel",
        handleWindowPointerCancel,
        true,
      );
    };
  }, [camera, finishDrag, gl.domElement, moveDrag]);

  React.useEffect(
    () => () => {
      const dragState = dragStateRef.current;
      if (dragState?.dragging) {
        endGroupTranslateIfNeeded(dragState);
        restoreDirtyBlocking(floorplannerStore, dragState);
        floorplannerStore.cancelUndoTransaction();
        onDragActiveChange?.(false);
      }
      dragStateRef.current = null;
      document.body.style.cursor = "auto";
    },
    [floorplannerStore, onDragActiveChange, symbol.id],
  );

  return {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: finishDrag,
    onPointerCancel: finishDrag,
  };
}

/**
 * Symbols that should move with the drag as one unit:
 * - the dragged symbol
 * - members of its explicit group (persistent group)
 * - other currently selected loose-draggable symbols (temporary multi-select unit)
 * - members of any explicit groups represented in the selection
 *
 * Temporary multi-select motion ends when selection clears; explicit groups stay in `groupsMap`.
 */
function getCoMovingSymbolDragStates(
  floorplannerStore: FloorplannerStore,
  draggedSymbol: SymbolType,
): Map<string, GroupedSymbolDragState> {
  const symbolIds = new Set<string>([draggedSymbol.id]);

  const addExplicitGroupMembers = (objectId: string) => {
    const groupId = floorplannerStore.getObjectGroup(objectId);
    if (!groupId) return;
    floorplannerStore.getGroupObjects(groupId).forEach((groupObject) => {
      if (isSymbolType(groupObject)) {
        symbolIds.add(groupObject.id);
      }
    });
  };

  addExplicitGroupMembers(draggedSymbol.id);

  editorStore.selections.forEach((selection) => {
    if (!isSymbolType(selection)) return;
    symbolIds.add(selection.id);
    addExplicitGroupMembers(selection.id);
  });

  const states = new Map<string, GroupedSymbolDragState>();
  symbolIds.forEach((symbolId) => {
    const currentSymbol = floorplannerStore.symbolsMap.get(symbolId);
    if (!currentSymbol || !isSymbolType(currentSymbol)) return;
    // Non-dragged members must be loose-draggable in 3D (skip doors/windows/etc.).
    if (
      symbolId !== draggedSymbol.id &&
      !isDraggableLoose3DSymbol(currentSymbol)
    ) {
      return;
    }
    states.set(currentSymbol.id, {
      startPosition: currentSymbol.position.clone(),
    });
  });

  if (!states.has(draggedSymbol.id)) {
    states.set(draggedSymbol.id, {
      startPosition: draggedSymbol.position.clone(),
    });
  }

  return states;
}

function getAttachmentWallIds(
  floorplannerStore: FloorplannerStore,
  groupedSymbols: Map<string, GroupedSymbolDragState>,
): Set<string> {
  const wallIds = new Set<string>();
  groupedSymbols.forEach((_, symbolId) => {
    const wallId = floorplannerStore.symbolIsAttached(symbolId);
    if (wallId) wallIds.add(wallId);
  });
  return wallIds;
}

/**
 * Commits sibling group members to their final store positions once (on pointer up).
 */
function commitGroupedSymbolOffset(
  floorplannerStore: FloorplannerStore,
  dragState: Symbol3DDragState,
  draggedSymbolId: string,
  offset: THREE.Vector2,
) {
  dragState.groupedSymbols.forEach((groupedSymbol, symbolId) => {
    if (symbolId === draggedSymbolId) return;
    const nextPosition = groupedSymbol.startPosition.clone().add(offset);
    floorplannerStore.updateSymbolPositionRespectingAttachment(symbolId, [
      nextPosition.x,
      nextPosition.y,
    ]);
  });
}

function updateGroupTranslateOffsetIfNeeded(
  dragState: Symbol3DDragState,
  offset: THREE.Vector2,
) {
  if (!dragState.usingGroupTranslate) return;
  renderStore.updateGroupTranslateOffset(offset.x, offset.y);
}

function endGroupTranslateIfNeeded(dragState: Symbol3DDragState) {
  if (!dragState.usingGroupTranslate) return;
  renderStore.endGroupTranslate();
  dragState.usingGroupTranslate = false;
}

function restoreDirtyBlocking(
  floorplannerStore: FloorplannerStore,
  dragState: Symbol3DDragState,
) {
  if (dragState.previousBlockDirty === null) return;
  floorplannerStore.setBlockDirty(dragState.previousBlockDirty);
  dragState.previousBlockDirty = null;
}

function applyNextVisualState(
  dragState: Symbol3DDragState,
  object: THREE.Object3D | null | undefined,
  applyVisualState:
    | ((object: THREE.Object3D, state: Symbol3DDragVisualState) => void)
    | undefined,
  state: Symbol3DDragVisualState,
) {
  dragState.visualState = state;
  if (object && applyVisualState) {
    applyVisualState(object, state);
  }
}

function projectPointToWall(point: THREE.Vector2, wall: WallType) {
  const segment = wall.end.clone().sub(wall.start);
  const lengthSq = segment.lengthSq();
  if (lengthSq <= 1e-8) {
    return {
      offset: 0,
      position: wall.start.clone(),
    };
  }

  const length = Math.sqrt(lengthSq);
  const direction = segment.clone().normalize();
  const offset = THREE.MathUtils.clamp(
    point.clone().sub(wall.start).dot(direction),
    0,
    length,
  );
  return {
    offset,
    position: wall.start.clone().add(direction.multiplyScalar(offset)),
  };
}

function getPointerPointOnFloorPlane(
  event: ThreeEvent<PointerEvent>,
): THREE.Vector3 | null {
  return event.ray.intersectPlane(FLOOR_PLANE, new THREE.Vector3());
}

function getPointerPointOnFloorPlaneFromNative(
  event: PointerEvent,
  camera: THREE.Camera,
  element: HTMLElement,
): THREE.Vector3 | null {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;

  const pointer = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1,
  );
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(pointer, camera);
  return raycaster.ray.intersectPlane(FLOOR_PLANE, new THREE.Vector3());
}

function setPointerCapture(event: ThreeEvent<PointerEvent>) {
  try {
    const pointerTarget = event.target as unknown as {
      setPointerCapture?: (pointerId: number) => void;
    };
    pointerTarget.setPointerCapture?.(event.pointerId);
  } catch {
    // Pointer capture can fail for synthetic or already-captured events.
  }
}

function releasePointerCapture(dragState: Symbol3DDragState) {
  try {
    dragState.pointerTarget?.releasePointerCapture?.(dragState.pointerId);
  } catch {
    // Pointer capture can already be released by the browser.
  }
}

function stopOrbitControlEvent(
  event?: ThreeEvent<PointerEvent> | PointerEvent,
) {
  if (!event) return;
  event.stopPropagation();
  if ("nativeEvent" in event) {
    if (event.nativeEvent.cancelable) {
      event.nativeEvent.preventDefault();
    }
    event.nativeEvent.stopImmediatePropagation?.();
    return;
  }
  if (event.cancelable) {
    event.preventDefault();
  }
  event.stopImmediatePropagation?.();
}
